/* eslint-disable prefer-destructuring */
import React, {useCallback, useEffect, useState} from 'react';
import Tabs from 'components/Tabs';
import {useDropzone} from 'react-dropzone';
import {getUserLogos, uploadUserLogo} from 'utils/fetch';
import CloseIcon from 'assets/svgs/actions/close.svg';
import {useInjectSaga} from 'utils/injectSaga';
import accountSaga from 'sagas/accountSaga';
import {useDispatch, useSelector} from 'react-redux';
import {changePassword, updateProfile, updateStatus} from 'actions/accountActions';
import PasswordInput from 'components/PasswordInput';
import {forgotPassword} from 'actions/userActions';
import OutsideClickHandler from 'react-outside-click-handler';

const EditProfile = ({userDetails, setEditProfile}) => {
  useInjectSaga({key: 'account', saga: accountSaga});
  const dispatch = useDispatch();
  const error = useSelector((state) => state.account.updateError);
  const [user, setUser] = useState(userDetails);
  const [localError, setLocalError] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState(userDetails.email);
  const [logoList, setLogoList] = useState({
    male: [],
    female: [],
  });

  const [resetDetails, setResetDetails] = useState({
    password: '',
    currentPassword: '',
    confirmPassword: '',
  });

  const [selectedImage, setSelectedImage] = useState(null);
  const [selectLogo, setSelectLogo] = useState(false);
  const uploadFile = async (acceptedFile) => {
    const savedFile = await uploadUserLogo({
      file: acceptedFile,
      authToken: userDetails.authToken,
      userId: userDetails._id,
    });

    const json = await savedFile.json();
    setSelectedImage(json.logo);
    handleChange('avatar', json.logo.liveURL);
  };

  const [passwordSection, setPasswordSection] = useState('PASSWORD');

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      uploadFile(file);
    }
  }, []);

  const getLogos = async () => {
    const logos = await getUserLogos({
      authToken: userDetails.authToken,
      searchKey: '',
    });
    // fix logos undefined error because home page aren't protected
    if (logos?.length) {
      const male = logos.filter((f) => f.logoType === 'Male');
      const female = logos.filter((f) => f.logoType === 'Female');
      setLogoList({male, female});
    }
  };

  useEffect(() => {
    getLogos();
  }, []);

  useEffect(() => {
    if (error) {
      setTimeout(() => dispatch(updateStatus()), 5000);
    }
    if (localError) {
      setTimeout(() => setLocalError(''), 5000);
    }
  }, [error, localError]);

  const {getRootProps, getInputProps} = useDropzone({
    onDrop,
    accept: 'image/jpeg, image/png, image/webp',
  });

  const [active, setActive] = useState({
    value: 'INFORMATION',
    label: 'INFORMATION',
  });

  const tabs = [
    {
      value: 'INFORMATION',
      label: 'INFORMATION',
    },
    {value: 'PASSWORD', label: 'PASSWORD'},
  ];

  const handleChange = (key, value) => {
    const newUser = {...user};
    if (key === 'name') {
      const names = value.split(' ');
      newUser.firstName = names[0];
      if (names.length > 1) {
        newUser.lastName = names.slice(1).join(' ');
      }
    } else {
      newUser[key] = value;
    }
    setUser(newUser);
  };

  const renderInformation = () => (
    <div className="edit-profile-content flex-column px-2">
      <div className="uploaded-img-avatar align-items-center d-flex justify-content-center mt-4 bg-white">
        <div
          className="h-100 w-100"
          {...getRootProps()}
          style={{borderRadius: '50%', marginRight: -2}}
        >
          <input {...getInputProps()} />
          {!selectedImage ? (
            <div className="d-flex justify-content-center align-items-center flex-column h-100 ">
              <p>Drop photo here</p>
              <p>min 250 x 250</p>
              <p>max 2mb</p>
            </div>
          ) : (
            <img
              src={`${selectedImage.liveURL}`}
              alt=""
              className="h-100 w-100"
            />
          )}
        </div>
      </div>

      <div className="w-100 mt-4 template">
        Or select an avatar from our{' '}
        <span
          aria-hidden
          onClick={() => setSelectLogo(true)}
          className="ml-1 select-from-avatar"
        >
          template
        </span>
      </div>

      {!selectLogo && (
        <div className="d-flex w-100 name-info flex-column mt-4">
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              type="text"
              className="form-control"
              // don't show firstName and lastName if aren't exist.
              id="name"
              value={
                (user.firstName || user.lastName) &&
                `${user.firstName ?? ''} ${user.lastName ?? ''}`
              }
              onChange={(e) => {
                handleChange('name', e.target.value);
              }}
              placeholder="Name"
            />
          </div>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="text"
              className="form-control"
              id="email"
              onChange={(e) => {
                handleChange('email', e.target.value);
              }}
              disabled
              value={user.email}
              placeholder="Email"
            />
          </div>
        </div>
      )}
      {selectLogo && (
        <div className="d-flex w-100 avatar-container mt-4 bg-white">
          <img
            src={CloseIcon}
            aria-hidden
            alt=""
            className="close-icon pointer"
            onClick={() => setSelectLogo(null)}
          />
          <div className="d-flex flex-column w-100">
            {logoList.female.map((avatar) => (
              <img
                key={avatar._id}
                alt={avatar.name}
                aria-hidden
                className={`${
                  selectedImage && selectedImage._id === avatar._id
                    ? 'active'
                    : ''
                }`}
                onClick={() => {
                  setSelectedImage(avatar);
                  handleChange('avatar', avatar.liveURL);
                }}
                src={`${avatar.liveURL}`}
              />
            ))}
          </div>
          <div className="d-flex flex-column w-100">
            {logoList.male.map((avatar) => (
              <img
                key={avatar._id}
                alt={avatar.name}
                aria-hidden
                className={`${
                  selectedImage && selectedImage._id === avatar._id
                    ? 'active'
                    : ''
                }`}
                onClick={() => {
                  setSelectedImage(avatar);
                  handleChange('avatar', avatar.liveURL);
                }}
                src={`${avatar.liveURL}`}
              />
            ))}
          </div>
        </div>
      )}
      {error && <div className="p-2 bg-gold">{error}</div>}
      <div className="d-flex w-100 mt-4">
        <button
          type="button"
          onClick={() => {
            dispatch(updateProfile(user));
            setSelectLogo(false);
          }}
          className="btn btn-dark w-100"
        >
          Save Changes
        </button>
      </div>
    </div>
  );

  const handlePasswordChange = (key, value) => {
    const newResetDetails = {...resetDetails};
    newResetDetails[key] = value;
    setResetDetails(newResetDetails);
  };

  const onChangePassword = () => {
    const isValid = Object.keys(resetDetails).find((f) => !resetDetails[f]);
    if (isValid) {
      setLocalError('All fields are required');
      return null;
    }

    if (resetDetails.password !== resetDetails.confirmPassword) {
      setLocalError("Password doesn't match");
      return null;
    }

    setLocalError(null);
    dispatch(changePassword({...resetDetails, _id: userDetails._id}));
    return null;
  };

  const passwordForm = () => (
    <div className="d-flex flex-column w-100 mt-4 passwords pt-4">
      <PasswordInput
        onChange={(e) => {
          handlePasswordChange('currentPassword', e.target.value);
        }}
        value={resetDetails.currentPassword}
        label="Current Password"
      />
      <PasswordInput
        onChange={(e) => {
          handlePasswordChange('password', e.target.value);
        }}
        value={resetDetails.password}
        label="New Password"
      />
      <PasswordInput
        onChange={(e) => {
          handlePasswordChange('confirmPassword', e.target.value);
        }}
        value={resetDetails.confirmPassword}
        label="New Password confirmation"
      />
      {error || localError ? (
        <div className="p-2 bg-gold">{error || localError}</div>
      ) : null}

      <div className="d-flex mt-4 w-100 justify-content-end">
        <button
          type="button"
          onClick={() => {
            setPasswordSection('RESET');
          }}
          className="btn-forgot"
        >
          Forgot Password?
        </button>
      </div>
      <div className="d-flex w-100 mt-4">
        <button
          type="button"
          onClick={onChangePassword}
          className="btn btn-dark w-100"
        >
          Save Changes
        </button>
      </div>
    </div>
  );

  const resetForm = () => (
    <div className="d-flex flex-column w-100 mt-4 passwords pt-4">
      <div className="form-group mt-4">
        <label htmlFor="email">Current Email</label>
        <input
          type="text"
          className="form-control"
          onChange={(e) => {
            setRecoveryEmail(e.target.value);
          }}
          value={recoveryEmail}
          placeholder="Email"
        />
      </div>

      <p>
        We will send a password recover link to your email, please check your
        email and reset password within 24 hours.
      </p>

      {error || localError ? (
        <div className="p-2 bg-gold">{error || localError}</div>
      ) : null}
      <div className="d-flex w-100 mt-4">
        <button
          type="button"
          onClick={() => {
            dispatch(forgotPassword(recoveryEmail));
            setPasswordSection('SEND');
          }}
          className="btn btn-dark w-100"
        >
          Recover Password
        </button>
      </div>
    </div>
  );

  const sendForm = () => (
    <div className="d-flex flex-column w-100 mt-4 bg-white p-2 px-4 template pt-4 check-mail">
      <p>
        Dear <span className="ml-1">{user.firstName}</span>
      </p>
      <p>
        We have sent out a letter about how to recover link to your email
        <span className="ml-1">{recoveryEmail}</span>.
      </p>
      <p>
        Please check your email to get your password or reset password within
        <span className="ml-1">24 hours.</span>
      </p>
      <p className="text-right">Your Jidipi Team</p>
    </div>
  );

  const renderPassword = () => (
    <div className="d-flex w-100 flex-column edit-profile-content px-2">
      {passwordSection === 'PASSWORD' && passwordForm()}
      {passwordSection === 'RESET' && resetForm()}
      {passwordSection === 'SEND' && sendForm()}
    </div>
  );

  return (
    <div className="d-flex flex-column edit-profile-section">
      <OutsideClickHandler onOutsideClick={() => setEditProfile(false)}>
        <Tabs
          tabs={tabs}
          active={active}
          className="full-width"
          onChange={(tab) => {
            setActive(tab);
            setPasswordSection('PASSWORD');
          }}
        />
        {active.value === 'INFORMATION'
          ? renderInformation()
          : renderPassword()}
      </OutsideClickHandler>
    </div>
  );
};

export default EditProfile;
